import { useEffect, useCallback, useRef, useState, useSyncExternalStore } from 'react';
import { useParams } from 'react-router-dom';
import { useEvent, useSavedEvents, useCloseOrFallback } from '../../hooks';
import { categoryColors, categoryLabels, formatDateRange } from '../../utils';
import { trackEvent } from '../../utils/analytics';
import { hasRealImage } from '../../utils/imageUtils';
import {
  removeSsrShell,
  clearDisputedDateHead,
  exposeSsrRoot,
  isSsrRootReleased,
  subscribeSsrRootRelease,
} from '../../utils/ssrShell';
import { useDiscoveryMode } from '../../contexts/discoveryStore';
import { resolveRegistryPair } from '../../utils/registryLookup';
import { DateUnderReviewNotice } from '../events/DateUnderReviewNotice';
import { Chip } from '../ui/Chip';
import { BuzzPin } from '../icons/BuzzPin';
import { EventMeta } from '../events/EventMeta';
import type { Event } from '@buzzmap/shared';
import styles from './EventModal.module.css';

interface ModalHeroProps {
  event: Event;
}

/**
 * Hero image area: shows event.imageUrl when present (16:9 cover).
 */
function ModalHero({ event }: ModalHeroProps) {
  return (
    <div
      className={styles.heroWithImage}
      style={{ backgroundImage: `url("${event.imageUrl}")` }}
      role="img"
      aria-label={event.title}
      data-testid="modal-hero-image"
    />
  );
}

/* ----- Header icon buttons (Save / Share / Close) ---------------------- */

interface HeaderActionsProps {
  event: Event | null;
  onClose: () => void;
}

function HeaderActions({ event, onClose }: HeaderActionsProps) {
  const { isSaved, toggleSaved } = useSavedEvents();
  const [shareStatus, setShareStatus] = useState<'idle' | 'copied' | 'error'>('idle');

  const saved = event ? isSaved(event.id) : false;

  const handleSave = useCallback(() => {
    if (event) toggleSaved(event.id);
  }, [event, toggleSaved]);

  const handleShare = useCallback(async () => {
    if (!event) return;
    const shareUrl = window.location.href;
    const shareData = {
      title: event.title,
      text: `${event.title} - ${event.location.city}`,
      url: shareUrl,
    };
    try {
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      setShareStatus('copied');
      setTimeout(() => setShareStatus('idle'), 2000);
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        setShareStatus('error');
        setTimeout(() => setShareStatus('idle'), 2000);
      }
    }
  }, [event]);

  const shareTitle =
    shareStatus === 'copied' ? 'Link copiato!' : shareStatus === 'error' ? 'Errore' : 'Condividi';

  return (
    <div className={styles.headerActions}>
      {event && (
        <>
          <button
            type="button"
            onClick={handleSave}
            title={saved ? 'Rimuovi dai salvati' : 'Salva'}
            aria-label={saved ? 'Rimuovi dai salvati' : 'Salva evento'}
            aria-pressed={saved}
            className={`${styles.headerIconBtn} ${
              saved ? `${styles.headerIconActive} ${styles.headerSaveBtn}` : ''
            }`}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill={saved ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            {saved && <span className={styles.headerSaveLabel}>Salvato</span>}
          </button>
          <button
            type="button"
            onClick={handleShare}
            title={shareTitle}
            aria-label="Condividi evento"
            className={`${styles.headerIconBtn} ${
              shareStatus === 'copied' ? styles.headerIconSuccess : ''
            } ${shareStatus === 'error' ? styles.headerIconError : ''}`}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {shareStatus === 'copied' ? (
                <polyline points="20 6 9 17 4 12" />
              ) : (
                <>
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </>
              )}
            </svg>
          </button>
        </>
      )}
      <button
        type="button"
        onClick={onClose}
        title="Chiudi"
        aria-label="Chiudi"
        className={styles.headerIconBtn}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}

function ModalShellLoading({ onClose }: { onClose: () => void }) {
  return (
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.modal} role="dialog" aria-modal="true" aria-busy="true">
        <HeaderActions event={null} onClose={onClose} />
        <div className={styles.loadingShell}>
          <div className={styles.spinner} aria-label="Caricamento evento" />
        </div>
      </div>
    </div>
  );
}

/**
 * EventModal — Desktop-only modal overlay for event detail.
 *
 * Mounted by App.tsx in the secondary <Routes> tree whenever the route
 * matches `/event/:id` on desktop (≥1024px). On desktop, EventPage is
 * never rendered for /event/:id: a synthetic background (Esplora) is used
 * when the user lands on the URL directly so the modal always overlays
 * a real page underneath.
 */
export function EventModal() {
  const { id } = useParams<{ id: string }>();
  const { event, loading, error, dateUnderReview } = useEvent(id ?? '');

  // On a direct deeplink App.tsx renders the modal over a *synthetic* Esplora
  // background that was never pushed to history, so there is nothing to pop —
  // close falls back to that same route to keep what the user sees consistent.
  //
  // T013 (v2): the no-history fallback resolves to the EVENT's canonical
  // territory instead of synthetic generic state (plan l.147 — "closes to its
  // current comune"): an exact name+province registry pair lookup. An
  // unresolvable pair, a registry fault, or legacy keeps the literal
  // `/esplora` (the v2 entry gate central-normalizes it like any generic
  // entry).
  const discoveryMode = useDiscoveryMode();
  const eventRef = useRef(event);
  eventRef.current = event;
  const resolveCloseFallback = useCallback((): string | Promise<string> => {
    const ev = eventRef.current;
    if (discoveryMode !== 'v2' || !ev) return '/esplora';
    return resolveRegistryPair(ev.location.city, ev.location.province ?? undefined)
      .then((hit) => hit?.path ?? '/esplora')
      .catch(() => '/esplora');
  }, [discoveryMode]);
  const close = useCloseOrFallback(resolveCloseFallback);

  // Is the app root usable? `false` only while the server handoff scaffold
  // still hides it (T102-INT). Declared early: the review-dialog focus and the
  // scroll lock / ESC below both wait for it.
  const rootReleased = useSyncExternalStore(subscribeSsrRootRelease, isSsrRootReleased, () => true);

  // Shell rule (R15): on a desktop deep link this modal — not EventPage —
  // is what renders `/event/:id`, so its `useEvent` decides about the
  // shell, and only once the event is settled, present and error-free.
  // Behind it the synthetic Esplora background never removes (`resolution`
  // is null). Full rule: `ssrShell.ts`.
  useEffect(() => {
    if (loading) return;
    if (error || !event) {
      // Evento assente o in errore: il guscio resta, ma lo sfondo (Esplora)
      // sotto di esso resta usabile (T102-INT). In revisione data decide
      // l'effetto qui sotto.
      if (!dateUnderReview) exposeSsrRoot();
      return;
    }
    removeSsrShell();
  }, [loading, error, event, dateUnderReview]);

  // T010 (R7): the desktop counterpart of EventPage's review rule — the
  // date-review answer is authoritative content, so once its notice has
  // rendered (this effect runs after that commit) the server shell, which
  // may still carry the disputed date, must go — and so must the cached
  // document's dated `<head>` (title/og metas/JSON-LD; scrub is
  // event-document-guarded inside). Loading, network errors and valid
  // content keep the rule above unchanged.
  useEffect(() => {
    if (!dateUnderReview) return;
    removeSsrShell();
    clearDisputedDateHead();
  }, [dateUnderReview]);

  // W1 (a11y): move focus into the review dialog on open and restore it on
  // close — same pattern as FilterPopup (save the previous activeElement,
  // focus the first button in the dialog — Chiudi — after its 100 ms delay,
  // restore on cleanup). Only the review state ships this fix; the normal
  // modal states share the gap and it is a recorded T010 follow-up.
  const reviewDialogRef = useRef<HTMLDivElement>(null);
  const reviewPreviousFocusRef = useRef<HTMLElement | null>(null);

  // Focus needs a usable root: on an `inert` root it would fail silently, so
  // the effect (and its 100 ms timer) starts only once the root is released.
  useEffect(() => {
    if (!dateUnderReview || !rootReleased) return;
    reviewPreviousFocusRef.current = document.activeElement as HTMLElement | null;
    const timer = setTimeout(() => {
      reviewDialogRef.current?.querySelector<HTMLElement>('button')?.focus();
    }, 100);
    return () => {
      clearTimeout(timer);
      reviewPreviousFocusRef.current?.focus();
      reviewPreviousFocusRef.current = null;
    };
  }, [dateUnderReview, rootReleased]);

  // ESC key + body scroll lock — only once the app root is usable (T102-INT).
  // On a server-rendered deep link the modal mounts inside a root that is
  // still invisible and `inert`, under the shell the visitor is reading:
  // locking the page or swallowing ESC then would freeze a page the modal is
  // not yet showing. `rootReleased` flips in the same commit that removes the
  // shell (or deliberately keeps it); documents without the scaffold are
  // released from the start, so nothing changes for them.
  // Also only while something is drawn: with the event absent the modal
  // renders nothing, and locking the page would trap the shell and the
  // background the visitor is left with.
  const modalDrawn = loading || Boolean(dateUnderReview) || (!error && Boolean(event));
  useEffect(() => {
    if (!rootReleased || !modalDrawn) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [close, rootReleased, modalDrawn]);

  // Track modal-view as event_view (parity with EventPage)
  useEffect(() => {
    if (event) {
      trackEvent('event_view', {
        event_id: event.id,
        category: event.category,
        city: event.location.city,
        surface: 'modal',
      });
    }
  }, [event]);

  if (loading) {
    return <ModalShellLoading onClose={close} />;
  }

  // T010 (R7): the event exists but its disputed date is under verification
  // (structured 409 DATE_UNDER_REVIEW). The modal stays open — close button
  // and backdrop click still work — and explains, instead of collapsing to
  // null below, which would look like a deleted event. No event data is
  // drawn, so no disputed date can leak through the overlay.
  if (dateUnderReview) {
    return (
      <div
        className={styles.backdrop}
        onClick={(e) => {
          if (e.target === e.currentTarget) close();
        }}
        data-testid="event-modal-backdrop"
      >
        <div
          ref={reviewDialogRef}
          className={styles.modal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="event-modal-review-title"
          data-testid="event-modal"
        >
          <HeaderActions event={null} onClose={close} />
          <div className={styles.body}>
            <DateUnderReviewNotice
              messageClassName={styles.title}
              messageId="event-modal-review-title"
            />
          </div>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      close();
    }
  };

  return (
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      data-testid="event-modal-backdrop"
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-modal-title"
        data-testid="event-modal"
      >
        <HeaderActions event={event} onClose={close} />

        {hasRealImage(event) ? <ModalHero event={event} /> : null}

        <div className={styles.body}>
          <header className={styles.header}>
            <div className={styles.categoryChip}>
              <Chip variant="category" color={categoryColors[event.category]}>
                {categoryLabels[event.category] || event.category}
              </Chip>
              {event.isUnverified && <span className={styles.unverifiedBadge}>Non verificato</span>}
            </div>
            <h1 id="event-modal-title" className={styles.title}>
              {event.title}
            </h1>
            <p className={styles.subtitle}>
              <span className={styles.subtitleItem}>
                <span className={styles.subtitleIcon}>📅</span>
                {formatDateRange(event.startDate, event.endDate)}
                {event.startTime && (
                  <span className={styles.subtitleTime}>
                    {' · '}
                    {event.startTime.slice(0, 5)}
                    {event.endTime ? `-${event.endTime.slice(0, 5)}` : ''}
                  </span>
                )}
              </span>
              <span className={styles.subtitleItem}>
                <BuzzPin size={14} className={styles.subtitleIcon} />
                {event.location.city}
                {event.location.province && ` (${event.location.province})`}
              </span>
            </p>
          </header>

          {/* Single-column content. The previous side-card ("Azioni" + "Vedi
              pagina completa") was removed — save/share now live in the
              modal header, and the standalone full-page link was dropped. */}
          <div className={styles.content}>
            <EventMeta event={event} />
          </div>
        </div>
      </div>
    </div>
  );
}
